import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ContactSection = () => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [showNotification, setShowNotification] = useState(false);

  const handleSocialClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3tpr0djnnh8vq2jupj0', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          sujet: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 md:py-20 bg-gray-50">
      {/* Notification */}
      {showNotification && (
        <div className="fixed top-4 right-4 bg-blue-600 text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2 max-w-xs sm:max-w-sm">
          <i className="ri-information-line"></i>
          <span className="text-sm sm:text-base">Pas encore disponible pour le moment</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-charcoal-800 mb-4 sm:mb-6">
            {t('common:contactTitle')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-charcoal-600 max-w-3xl mx-auto leading-relaxed px-4">
            {t('common:contactDescription')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12">
          {/* Formulaire de contact */}
          <div className="bg-sand-50 rounded-2xl p-4 sm:p-6 md:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-charcoal-800 mb-4 sm:mb-6">{t('common:sendMessage')}</h3>
            
            <form id="contact-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="contact-nom" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:fullName')} {t('common:required')}
                  </label>
                  <input
                    type="text"
                    id="contact-nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs sm:text-sm"
                    placeholder={t('common:placeholderName')}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:email')} {t('common:required')}
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs sm:text-sm"
                    placeholder={t('common:placeholderEmail')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                <div>
                  <label htmlFor="contact-telephone" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:phone')}
                  </label>
                  <input
                    type="tel"
                    id="contact-telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs sm:text-sm"
                    placeholder={t('common:placeholderPhone')}
                  />
                </div>
                <div>
                  <label htmlFor="contact-sujet" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                    {t('common:subject')} {t('common:required')}
                  </label>
                  <div className="relative">
                    <select
                      id="contact-sujet"
                      name="sujet"
                      value={formData.sujet}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs sm:text-sm pr-8 appearance-none"
                    >
                      <option value="">{t('common:selectSubject')}</option>
                      <option value="Demande de devis">{t('common:quoteRequest')}</option>
                      <option value="Information service">{t('common:serviceInfo')}</option>
                      <option value="Réclamation">{t('common:complaint')}</option>
                      <option value="Partenariat">{t('common:partnership')}</option>
                      <option value="Autre">{t('common:other')}</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-charcoal-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>

              <div className="mb-4 sm:mb-6">
                <label htmlFor="contact-message" className="block text-xs sm:text-sm font-medium text-charcoal-700 mb-2">
                  {t('common:messageRequired')} {t('common:required')}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-sand-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs sm:text-sm resize-none"
                  placeholder={t('common:messageContactDescription')}
                ></textarea>
                <div className="text-right text-xs text-charcoal-500 mt-1">
                  {formData.message.length}/500 {t('common:charactersLimit')}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gold-500 text-white py-3 sm:py-4 rounded-lg hover:bg-gold-600 transition-colors font-semibold text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    {t('common:sending')}
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    {t('common:sendMessageBtn')}
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-3 sm:p-4 bg-green-100 border border-green-300 rounded-lg text-green-700 text-xs sm:text-sm">
                  <i className="ri-check-line mr-2"></i>
                  {t('common:successContactMessage')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-3 sm:p-4 bg-red-100 border border-red-300 rounded-lg text-red-700 text-xs sm:text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  {t('common:errorMessage')}
                </div>
              )}
            </form>
          </div>

          {/* Informations et carte */}
          <div className="space-y-6 sm:space-y-8">
            {/* Informations de contact */}
            <div className="bg-charcoal-800 text-white rounded-2xl p-4 sm:p-6 md:p-8">
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">{t('common:ourCoordinates')}</h3>
              
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gold-400 mb-1 text-sm sm:text-base">{t('common:address')}</h4>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base">
                      Casablanca, Maroc<br />
                      Montréal, Canada
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gold-400 mb-1 text-sm sm:text-base">{t('common:telephone')}</h4>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base">+212 666-204299</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gold-400 mb-1 text-sm sm:text-base">{t('common:email')}</h4>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base break-all">contact@hafytours.com</p>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base break-all">youssefhajir@yahoo.fr</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gold-400 mb-1 text-sm sm:text-base">{t('common:hours')}</h4>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base">Service 24h/24 - 7j/7</p>
                    <p className="text-gray-300 text-xs sm:text-sm md:text-base">{t('common:officeHours')}</p>
                  </div>
                </div>
              </div>

              {/* Réseaux sociaux */}
              <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-600">
                <h4 className="font-semibold text-gold-400 mb-3 sm:mb-4 text-sm sm:text-base">{t('common:followUs')}</h4>
                <div className="flex flex-wrap gap-3 sm:gap-4">
                  <button onClick={handleSocialClick} className="w-10 h-10 bg-gold-500 rounded-full flex items-center justify-center hover:bg-gold-600 transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-white"></i>
                  </button>
                  <button onClick={handleSocialClick} className="w-10 h-10 bg-gold-500 rounded-full flex items-center justify-center hover:bg-gold-600 transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-white"></i>
                  </button>
                  <a href="https://wa.me/212666204299" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gold-500 rounded-full flex items-center justify-center hover:bg-gold-600 transition-colors cursor-pointer">
                    <i className="ri-whatsapp-line text-white"></i>
                  </a>
                  <button onClick={handleSocialClick} className="w-10 h-10 bg-gold-500 rounded-full flex items-center justify-center hover:bg-gold-600 transition-colors cursor-pointer">
                    <i className="ri-tiktok-line text-white"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Carte Google Maps */}
            <div className="bg-sand-50 rounded-2xl p-4 sm:p-6 md:p-8">
              <h3 className="text-lg sm:text-xl font-bold text-charcoal-800 mb-3 sm:mb-4">{t('common:ourLocation')}</h3>
              <div className="w-full h-48 sm:h-56 md:h-64 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3323.0969259571!2d-7.6261!3d33.5731!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xda7cd4778aa113b%3A0xb06c1d84f310fd3!2sCasablanca%2C%20Morocco!5e0!3m2!1sen!2s!4v1635000000000!5m2!1sen!2s"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation Transport Premium"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
